import { bindRecoveryMergeFunding } from "./sdk";
import { prepareAtomicEvmRecovery } from "./onchainRecoveryEvmPublish";
import { formatInboxPublicKeyHex } from "../utils/inbox-crypto";
import { ethers } from "ethers";
import type {
  JoinSplitNote,
  SpendingKeys,
  StoredJoinSplitNote,
} from "../joinsplit/types";
import { computeNullifier } from "../joinsplit/keys";
import { candidateNotes } from "./noteCandidates";
import { authenticateAndDedupeCandidates } from "./noteSelection";
import {
  createFlatJoinSplitV2Note,
  flatFieldHex,
  BN254_SCALAR_FIELD,
} from "./notes";
import { loadSpendTree } from "./poolAccess";
import { inputWitness, validateInputs } from "./witness";
import {
  authorizeFlatDepositMergeJournal,
  prepareFlatJoinSplitV2SettlementJournal,
  markFlatJoinSplitV2SettlementDisclosed,
  archiveFlatJoinSplitV2UndisclosedPreparation,
  reservedFlatJoinSplitV2Commitments,
} from "./pendingJournal";
import { persistFlatJournalBackup } from "./journalBackup";
import { walletSettlementErrorWithContext } from "./walletSubmission";
import {
  authorizePrivateDepositMerge,
  depositMergeFeeNotes,
  preparePrivateDepositMergeQuote,
  privateDepositMergeRecoveryTemplate,
  type PrivateDepositMergeQuote,
} from "./privateFeeDepositMerge";
import {
  planDepositMerge,
  type DepositMergeCapability,
  type DepositMergeCandidate,
  type DepositMergeScope,
} from "./depositMergePlan";
import {
  createDepositMergeProverFromEnv,
  depositMergeProverConfigFromEnv,
  type DepositMergeProofResponse,
  type DepositMergeProver,
} from "./depositMergeProver";

const VALUE_LIMIT = 1n << 120n;
const U64_LIMIT = 1n << 64n;
const FQ =
  21888242871839275222246405745257275088696311157297823662689037894645226208583n;
const ZERO_WORD = `0x${"00".repeat(32)}` as const;
const FAMILY = "deposit-merge-v1" as const;
const FUNDING_DOMAIN = "STEALTHOS_DEPOSIT_MERGE_FUNDING_V1";
const STATEMENT_DOMAIN = 0x53544f53444d5631n;

export const EVM_DEPOSIT_MERGE_INTERFACE = new ethers.Interface([
  "function depositMergeVersion() view returns(uint256)",
  "function depositMergeFeeMode() view returns(uint256)",
  "function depositMergeVerifier() view returns(address)",
  "function depositAndMerge((bytes32 merkleRoot,bytes32 nullifier,bytes32 commitment,address token,uint256 amount,uint64 deadline,bytes32 nonce,bytes proof) data) payable",
  "event DepositMerged(uint256 indexed treeIndex,bytes32 indexed nullifier,bytes32 commitment,uint256 leafIndex,address token,uint256 netCredit)",
  "event LeafInsertedInTree(uint256 indexed treeIndex,bytes32 leaf,uint256 leafIndex)",
]);

export interface EvmDepositMergePrepared {
  note: JoinSplitNote;
  input: StoredJoinSplitNote;
  sourceNullifier: `0x${string}`;
  merkleRoot: `0x${string}`;
  tokenHash: `0x${string}`;
  transactionData: `0x${string}`;
  deadline: bigint;
  nonce: `0x${string}`;
  provingTimeMs: number;
  netCredit: bigint;
  feeQuote?: PrivateDepositMergeQuote;
  /** Already durably backed up before private-fee authorization. */
  journalId?: string;
}

export interface EvmDepositMergeEvent {
  treeIndex: number;
  leafIndex: number;
}

export interface PrepareEvmDepositMergeParams {
  provider: ethers.Provider;
  chainId: bigint;
  pool: string;
  token: string;
  payer: string;
  netCredit: bigint;
  senderMpk: bigint;
  recipientMpk: bigint;
  spendingKeys: SpendingKeys;
  notes: StoredJoinSplitNote[];
  tokenDecimals?: number;
  privateHistory?: JoinSplitNote["privateHistory"];
  signal?: AbortSignal;
  onProgress?: (phase: "loading" | "proving" | "checking") => void;
  /** Test seam. Production leaves this undefined. */
  createProver?: () => DepositMergeProver | null;
  privateFees?: {
    grossAmount: bigint;
    inboxPublicKey: string;
    proveFee: (note: JoinSplitNote, recoveryDigest?: string) => Promise<string>;
  };
}

function fail(message: string): never {
  throw new Error(`Deposit merge rejected: ${message}`);
}

function word(value: bigint, field = false, nonzero = false): `0x${string}` {
  if (
    value < 0n ||
    (field && value >= BN254_SCALAR_FIELD) ||
    (nonzero && value === 0n)
  ) {
    fail("deposit merge word is outside its allowed range");
  }
  return ethers.toBeHex(value, 32) as `0x${string}`;
}

function addressWord(address: string): `0x${string}` {
  return ethers
    .zeroPadValue(ethers.getAddress(address), 32)
    .toLowerCase() as `0x${string}`;
}

function uint(value: bigint, limit: bigint, nonzero = false): bigint {
  if (value < (nonzero ? 1n : 0n) || value >= limit) {
    fail("deposit merge integer is outside its allowed range");
  }
  return value;
}

export function computeEvmDepositMergeFundingContext(params: {
  chainId: bigint;
  pool: string;
  treeIndex: bigint;
  token: string;
  payer: string;
  grossAmount: bigint;
  netCredit: bigint;
  feeCommitmentsHash?: string;
  deadline: bigint;
  nonce: `0x${string}`;
}): `0x${string}` {
  const fields = [
    word(uint(params.chainId, U64_LIMIT, true)),
    addressWord(params.pool),
    word(uint(params.treeIndex, U64_LIMIT)),
    addressWord(params.token),
    addressWord(params.payer),
    word(uint(params.grossAmount, VALUE_LIMIT, true)),
    word(uint(params.netCredit, VALUE_LIMIT, true)),
    params.feeCommitmentsHash ?? ZERO_WORD,
    word(uint(params.deadline, U64_LIMIT, true)),
    params.nonce,
  ];
  if (!ethers.isHexString(params.feeCommitmentsHash ?? ZERO_WORD, 32)) {
    fail("deposit merge fee commitment binding is invalid");
  }
  if (!ethers.isHexString(params.nonce, 32) || BigInt(params.nonce) === 0n) {
    fail("deposit merge nonce is invalid");
  }
  if (params.netCredit > params.grossAmount) {
    fail("deposit merge credit exceeds deposit funding");
  }
  const digest = BigInt(
    ethers.sha256(
      ethers.concat([ethers.toUtf8Bytes(FUNDING_DOMAIN), ...fields]),
    ),
  );
  return word(digest % BN254_SCALAR_FIELD, true, true);
}

let poseidonPromise: Promise<any> | null = null;
async function poseidon2(left: bigint, right: bigint): Promise<bigint> {
  poseidonPromise ??= import("circomlibjs").then(async (module) => {
    const typed = module as unknown as { buildPoseidon: () => Promise<any> };
    return typed.buildPoseidon();
  });
  const poseidon = await poseidonPromise;
  const output = poseidon([left, right]);
  return BigInt(poseidon.F.toObject(output).toString());
}

async function expectedPublicInputs(params: {
  merkleRoot: `0x${string}`;
  nullifier: `0x${string}`;
  commitment: `0x${string}`;
  tokenHash: `0x${string}`;
  netCredit: bigint;
  fundingContext: `0x${string}`;
}): Promise<`0x${string}`[]> {
  const fields = [
    params.merkleRoot,
    params.nullifier,
    params.commitment,
    params.tokenHash,
    word(params.netCredit, true, true),
    params.fundingContext,
  ] as `0x${string}`[];
  let statement = STATEMENT_DOMAIN;
  for (const field of fields) {
    statement = await poseidon2(statement, BigInt(field));
  }
  return [...fields, word(statement, true)];
}

function canonicalProof(
  value: DepositMergeProofResponse,
  expected: readonly `0x${string}`[],
): `0x${string}` {
  if (
    value?.family !== FAMILY ||
    value.locallyVerified !== true ||
    typeof value.proof !== "string" ||
    !/^0x[0-9a-fA-F]{512}$/.test(value.proof) ||
    !Array.isArray(value.publicInputs) ||
    value.publicInputs.length !== 7
  ) {
    fail("deposit merge prover returned a malformed response");
  }
  const actual = value.publicInputs.map((field) => {
    if (
      !/^0x[0-9a-fA-F]{64}$/.test(field) ||
      BigInt(field) >= BN254_SCALAR_FIELD
    ) {
      fail("deposit merge prover returned a noncanonical public input");
    }
    return field.toLowerCase();
  });
  if (actual.some((field, index) => field !== expected[index].toLowerCase())) {
    fail("deposit merge proof does not match the intended funding");
  }
  for (let offset = 2; offset < value.proof.length; offset += 64) {
    if (BigInt(`0x${value.proof.slice(offset, offset + 64)}`) >= FQ) {
      fail("deposit merge prover returned a noncanonical proof coordinate");
    }
  }
  return value.proof.toLowerCase() as `0x${string}`;
}

export async function readEvmDepositMergeCapability(
  provider: ethers.Provider,
  poolAddress: string,
  options: { privateFeesReady?: boolean; runtimeReady?: boolean } = {},
): Promise<DepositMergeCapability | null> {
  const pool = ethers.getAddress(poolAddress);
  try {
    const contract = new ethers.Contract(
      pool,
      EVM_DEPOSIT_MERGE_INTERFACE,
      provider,
    );
    const [version, feeMode, verifier] = await Promise.all([
      contract.depositMergeVersion(),
      contract.depositMergeFeeMode(),
      contract.depositMergeVerifier(),
    ]);
    if (
      BigInt(version) !== 1n ||
      (BigInt(feeMode) !== 0n &&
        !(BigInt(feeMode) === 1n && options.privateFeesReady)) ||
      !ethers.isAddress(verifier) ||
      ethers.getAddress(verifier) === ethers.ZeroAddress ||
      (await provider.getCode(verifier)) === "0x"
    ) {
      return null;
    }
    if (
      !options.runtimeReady &&
      (!depositMergeProverConfigFromEnv() ||
        !(await createDepositMergeProverFromEnv()))
    )
      return null;
    const network = await provider.getNetwork();
    return {
      family: FAMILY,
      network: `evm:${network.chainId}`,
      pool,
      enabled: true,
      feeMode: BigInt(feeMode) === 0n ? "none" : "private-fees",
      feeAdapterReady: true,
      recoveryReady: true,
    };
  } catch {
    return null;
  }
}

export async function prepareEvmDepositMerge(
  params: PrepareEvmDepositMergeParams,
): Promise<EvmDepositMergePrepared | null> {
  if (params.signal?.aborted)
    throw new DOMException("Deposit cancelled", "AbortError");
  if (params.senderMpk !== params.recipientMpk) return null;
  if (
    params.netCredit <= 0n ||
    params.netCredit >= VALUE_LIMIT ||
    params.spendingKeys.mpk !== params.senderMpk
  ) {
    return null;
  }
  const pool = ethers.getAddress(params.pool);
  const token = ethers.getAddress(params.token);
  const payer = ethers.getAddress(params.payer);
  const prover =
    params.createProver?.() ?? (await createDepositMergeProverFromEnv());
  if (!prover) return null;
  const capability = await readEvmDepositMergeCapability(
    params.provider,
    pool,
    {
      privateFeesReady: !!params.privateFees,
      runtimeReady: true,
    },
  );
  if (!capability) return null;
  if ((capability.feeMode === "private-fees") !== !!params.privateFees)
    return null;

  const contract = new ethers.Contract(
    pool,
    [
      "function currentTreeIndex() view returns(uint256)",
      "function currentTokenHash(address token) view returns(bytes32)",
    ],
    params.provider,
  );
  const [treeRaw, tokenHashRaw] = await Promise.all([
    contract.currentTreeIndex(),
    contract.currentTokenHash(token),
  ]);
  const treeIndex = Number(treeRaw);
  const tokenHash = String(tokenHashRaw).toLowerCase();
  if (
    !Number.isSafeInteger(treeIndex) ||
    treeIndex < 0 ||
    !/^0x[0-9a-f]{64}$/.test(tokenHash) ||
    BigInt(tokenHash) <= 0n ||
    BigInt(tokenHash) >= BN254_SCALAR_FIELD
  ) {
    fail("pool returned an invalid deposit merge domain");
  }

  const authenticated = await authenticateAndDedupeCandidates({
    candidates: candidateNotes(params.notes, pool, params.chainId, token),
    pool,
    chainId: params.chainId,
    tokenAddress: token,
    spendingKeys: params.spendingKeys,
  });
  const scope: DepositMergeScope = {
    network: `evm:${params.chainId}`,
    pool,
    tokenHash: BigInt(tokenHash),
    treeIndex,
  };
  const reserved = await reservedFlatJoinSplitV2Commitments({
    chainId: params.chainId,
    poolAddress: pool,
    accountAddress: payer,
    spendingKey: params.spendingKeys.spendingKey,
  });
  const candidates: DepositMergeCandidate[] = authenticated.map((stored) => ({
    stored,
    scope,
    confirmed: stored.note.leafIndex >= 0,
    reserved: reserved.has(stored.note.commitment.toLowerCase()),
  }));
  const plan = planDepositMerge({
    scope,
    senderMpk: params.senderMpk,
    recipientMpk: params.recipientMpk,
    netCredit: params.netCredit,
    candidates,
    capability,
  });
  if (plan.mode !== "merge") return null;

  params.onProgress?.("loading");
  const tree = await loadSpendTree(params.chainId, pool, treeIndex);
  await validateInputs({
    provider: params.provider,
    pool,
    tokenAddress: token,
    spendingKeys: params.spendingKeys,
    selected: [plan.input],
    expectedTokenHash: tokenHash,
  });
  const witness = inputWitness(
    plan.input.note,
    tree.path(plan.input.note.commitment),
    params.spendingKeys,
  );
  const sourceNullifier = (
    await computeNullifier(
      params.spendingKeys.nullifyingKey,
      plan.input.note.tokenHash,
      plan.input.note.leafIndex,
    )
  ).toLowerCase() as `0x${string}`;

  const feeQuote = params.privateFees
    ? await preparePrivateDepositMergeQuote({
        provider: params.provider,
        chainId: params.chainId,
        pool,
        token,
        payer,
        treeIndex,
        tokenHash,
        merkleRoot: tree.root,
        nullifier: sourceNullifier,
        grossAmount: params.privateFees.grossAmount,
        signal: params.signal,
      })
    : undefined;
  const netCredit = feeQuote
    ? BigInt(feeQuote.amounts.recipientAmount)
    : params.netCredit;
  const outputValue = plan.input.note.value + netCredit;
  if (outputValue >= VALUE_LIMIT) return null;
  const note = {
    ...(await createFlatJoinSplitV2Note({
      recipientMpk: params.spendingKeys.mpk,
      chainId: params.chainId,
      poolAddress: pool,
      treeIndex,
      tokenAddress: token,
      tokenHash,
      value: outputValue,
    })),
    ...(params.privateHistory
      ? { privateHistory: { ...params.privateHistory } }
      : {}),
  };
  const recovery = await prepareAtomicEvmRecovery({
    provider: params.provider,
    chainId: Number(params.chainId),
    pool,
    outputs: [
      {
        note,
        inboxPublicKey: formatInboxPublicKeyHex(
          params.spendingKeys.spendingKey,
        ),
      },
    ],
    feeOutputs: feeQuote?.feeOutputs,
    signal: params.signal,
  });
  const deadline = feeQuote
    ? BigInt(feeQuote.depositMerge.deadline)
    : BigInt(Math.floor(Date.now() / 1000) + 15 * 60);
  const nonce = (feeQuote?.depositMerge.nonce ??
    ethers.hexlify(ethers.randomBytes(32)).toLowerCase()) as `0x${string}`;
  const originalFundingContext = computeEvmDepositMergeFundingContext({
    chainId: params.chainId,
    pool,
    treeIndex: BigInt(treeIndex),
    token,
    payer,
    grossAmount: feeQuote
      ? BigInt(feeQuote.amounts.grossAmount)
      : params.netCredit,
    netCredit,
    feeCommitmentsHash: feeQuote?.depositMerge.feeCommitmentsHash,
    deadline,
    nonce,
  });
  if (
    feeQuote &&
    originalFundingContext !==
      feeQuote.depositMerge.fundingContext.toLowerCase()
  ) {
    fail("deposit fee quote has a different funding context");
  }
  const fundingContext = (
    recovery
      ? bindRecoveryMergeFunding(originalFundingContext, recovery.digest)
      : originalFundingContext
  ) as `0x${string}`;
  const expected = await expectedPublicInputs({
    merkleRoot: tree.root as `0x${string}`,
    nullifier: sourceNullifier,
    commitment: note.commitment as `0x${string}`,
    tokenHash: tokenHash as `0x${string}`,
    netCredit,
    fundingContext,
  });

  params.onProgress?.("proving");
  const provingStarted = Date.now();
  const proofEnvelope = await prover.prove(
    {
      merkle_root: BigInt(tree.root).toString(),
      input: {
        npk: BigInt(witness.npk).toString(),
        token_hash: BigInt(witness.token_hash).toString(),
        value: String(witness.value),
        random: BigInt(witness.random).toString(),
        leaf_index: String(witness.leaf_index),
        nullifying_key: BigInt(witness.nullifying_key).toString(),
        pub_key_x: BigInt(witness.pub_key_x).toString(),
        pub_key_y: BigInt(witness.pub_key_y).toString(),
        path_elements: witness.path_elements.map((value) =>
          BigInt(value).toString(),
        ),
        path_indices: [...witness.path_indices],
      },
      spend_secret_scalar: params.spendingKeys.spendSecretScalar.toString(),
      output_random: note.random.toString(),
      output_value: note.value.toString(),
      net_credit: netCredit.toString(),
      funding_context: BigInt(fundingContext).toString(),
    },
    params.signal,
  );
  const proof = canonicalProof(proofEnvelope, expected);
  params.onProgress?.("checking");

  const mergeData = {
    merkleRoot: tree.root,
    nullifier: sourceNullifier,
    commitment: note.commitment,
    token,
    amount: netCredit.toString(),
    deadline: deadline.toString(),
    nonce,
    proof,
  };
  let data = EVM_DEPOSIT_MERGE_INTERFACE.encodeFunctionData("depositAndMerge", [
    mergeData,
  ]) as `0x${string}`;
  let journalId: string | undefined;
  if (feeQuote && params.privateFees) {
    const fees = depositMergeFeeNotes(feeQuote);
    const feeProofs: string[] = [];
    for (const fee of fees)
      feeProofs.push(await params.privateFees.proveFee(fee, recovery?.digest));
    const feeData = {
      merge: mergeData,
      tokenHash,
      feeCommitments: fees.map((fee) => fee.commitment),
      feeAmounts: fees.map((fee) => fee.value.toString()),
      feeProofs,
    };
    const journalScope = {
      chainId: params.chainId,
      poolAddress: pool,
      accountAddress: payer,
      spendingKey: params.spendingKeys.spendingKey,
    };
    try {
      data = await authorizePrivateDepositMerge({
        quote: feeQuote,
        recovery,
        note,
        recipientMpk: params.spendingKeys.mpk,
        inboxPublicKey: params.privateFees.inboxPublicKey,
        signal: params.signal,
        data: feeData,
        publicInputs: expected,
        beforeAuthorize: async () => {
          const journal = await prepareFlatJoinSplitV2SettlementJournal(
            journalScope,
            {
              kind: "deposit",
              proofDisclosure: "undisclosed",
              transaction: {
                to: pool,
                from: payer,
                chainId: Number(params.chainId),
                value:
                  token === ethers.ZeroAddress
                    ? feeQuote.amounts.grossAmount
                    : "0",
                data: recovery
                  ? recovery.wrap(
                      privateDepositMergeRecoveryTemplate(feeQuote, feeData),
                    )
                  : privateDepositMergeRecoveryTemplate(feeQuote, feeData),
              },
              sourceCommitments: [plan.input.note.commitment],
              sourceNullifiers: [sourceNullifier],
              outputNotes: [note],
              deliveries: [
                {
                  commitment: note.commitment,
                  recipientMpkHex: flatFieldHex(params.spendingKeys.mpk),
                  keepLocal: true,
                },
              ],
            },
          );
          journalId = journal.id;
          await persistFlatJournalBackup(journalScope, journal.id, {
            signal: params.signal,
          });
          await markFlatJoinSplitV2SettlementDisclosed(
            journalScope,
            journal.id,
          );
        },
      });
      await authorizeFlatDepositMergeJournal(journalScope, journalId!, data);
    } catch (error) {
      if (journalId) {
        await archiveFlatJoinSplitV2UndisclosedPreparation(
          journalScope,
          journalId,
        ).catch(() => {});
        throw walletSettlementErrorWithContext(error, journalId);
      }
      throw error;
    }
  }

  if (recovery && !feeQuote) data = recovery.wrap(data);
  return {
    note,
    input: plan.input,
    sourceNullifier,
    merkleRoot: tree.root as `0x${string}`,
    tokenHash: tokenHash as `0x${string}`,
    transactionData: data,
    deadline,
    nonce,
    provingTimeMs: Date.now() - provingStarted,
    netCredit,
    feeQuote,
    journalId,
  };
}

export function parseEvmDepositMergeReceipt(params: {
  receipt: ethers.TransactionReceipt;
  pool: string;
  expectedTreeIndex: number;
  expectedNullifier: string;
  expectedCommitment: string;
  token: string;
  netCredit: bigint;
}): EvmDepositMergeEvent {
  const pool = ethers.getAddress(params.pool);
  const token = ethers.getAddress(params.token);
  const mergeFragment = EVM_DEPOSIT_MERGE_INTERFACE.getEvent("DepositMerged")!;
  const leafFragment =
    EVM_DEPOSIT_MERGE_INTERFACE.getEvent("LeafInsertedInTree")!;
  const matching = (fragment: ethers.EventFragment) =>
    params.receipt.logs
      .filter(
        (log) =>
          log.address.toLowerCase() === pool.toLowerCase() &&
          log.topics[0]?.toLowerCase() === fragment.topicHash.toLowerCase(),
      )
      .map((log) => {
        if (
          log.removed ||
          log.transactionHash?.toLowerCase() !==
            params.receipt.hash.toLowerCase() ||
          log.blockHash?.toLowerCase() !==
            params.receipt.blockHash.toLowerCase() ||
          log.blockNumber !== params.receipt.blockNumber
        ) {
          fail("deposit merge log is not part of the canonical receipt");
        }
        return EVM_DEPOSIT_MERGE_INTERFACE.decodeEventLog(fragment, log.data, [
          ...log.topics,
        ]);
      });
  const merges = matching(mergeFragment).filter(
    (event) =>
      String(event.nullifier).toLowerCase() ===
        params.expectedNullifier.toLowerCase() &&
      String(event.commitment).toLowerCase() ===
        params.expectedCommitment.toLowerCase(),
  );
  const leaves = matching(leafFragment).filter(
    (event) =>
      String(event.leaf).toLowerCase() ===
      params.expectedCommitment.toLowerCase(),
  );
  if (merges.length !== 1 || leaves.length !== 1) {
    fail(
      "receipt does not contain exactly one matching merge and leaf insertion",
    );
  }
  const merge = merges[0];
  const leaf = leaves[0];
  const treeIndex = Number(merge.treeIndex);
  const leafIndex = Number(merge.leafIndex);
  if (
    !Number.isSafeInteger(treeIndex) ||
    treeIndex !== params.expectedTreeIndex ||
    !Number.isSafeInteger(leafIndex) ||
    leafIndex < 0 ||
    leafIndex >= 1 << 24 ||
    Number(leaf.treeIndex) !== treeIndex ||
    Number(leaf.leafIndex) !== leafIndex ||
    ethers.getAddress(merge.token) !== token ||
    BigInt(merge.netCredit) !== params.netCredit
  ) {
    fail("deposit merge receipt does not match the prepared transaction");
  }
  return { treeIndex, leafIndex };
}

export async function verifyEvmDepositMergeReceipt(params: {
  provider: ethers.Provider;
  receipt: ethers.TransactionReceipt;
  chainId: bigint | number;
  pool: string;
  note: Pick<
    JoinSplitNote,
    "commitment" | "tokenData" | "tokenHash" | "treeIndex"
  >;
  sourceNullifier: string;
  netCredit: bigint;
}): Promise<EvmDepositMergeEvent> {
  const { provider, receipt, note } = params;
  const pool = ethers.getAddress(params.pool);
  if (
    !receipt ||
    receipt.status !== 1 ||
    !ethers.isHexString(receipt.hash, 32) ||
    !ethers.isHexString(receipt.blockHash, 32) ||
    !Number.isSafeInteger(receipt.blockNumber) ||
    receipt.blockNumber < 0 ||
    note.treeIndex === undefined ||
    !Number.isSafeInteger(note.treeIndex) ||
    note.treeIndex < 0 ||
    !note.tokenData ||
    note.tokenHash <= 0n ||
    note.tokenHash >= BN254_SCALAR_FIELD
  ) {
    fail("canonical deposit merge receipt metadata is unavailable");
  }
  const [network, block, transaction] = await Promise.all([
    provider.getNetwork(),
    provider.getBlock(receipt.blockNumber),
    provider.getTransaction(receipt.hash),
  ]);
  if (
    BigInt(network.chainId) !== BigInt(params.chainId) ||
    !transaction ||
    transaction.chainId == null ||
    BigInt(transaction.chainId) !== BigInt(params.chainId) ||
    transaction.hash.toLowerCase() !== receipt.hash.toLowerCase() ||
    transaction.blockNumber !== receipt.blockNumber ||
    transaction.blockHash?.toLowerCase() !== receipt.blockHash.toLowerCase() ||
    transaction.to?.toLowerCase() !== receipt.to?.toLowerCase() ||
    transaction.from.toLowerCase() !== receipt.from.toLowerCase() ||
    block?.number !== receipt.blockNumber ||
    block.hash?.toLowerCase() !== receipt.blockHash.toLowerCase()
  ) {
    fail("deposit merge receipt transaction or chain is inconsistent");
  }
  const event = parseEvmDepositMergeReceipt({
    receipt,
    pool,
    expectedTreeIndex: note.treeIndex,
    expectedNullifier: params.sourceNullifier,
    expectedCommitment: note.commitment,
    token: note.tokenData.tokenAddress,
    netCredit: params.netCredit,
  });
  const domainInterface = new ethers.Interface([
    "function computeTokenHashForTree(uint256 treeIndex,address token,uint8 tokenType,uint256 tokenSubID) view returns(bytes32)",
  ]);
  const raw = await provider.call({
    to: pool,
    blockTag: receipt.blockNumber,
    data: domainInterface.encodeFunctionData("computeTokenHashForTree", [
      note.treeIndex,
      note.tokenData.tokenAddress,
      0,
      0,
    ]),
  });
  const [domain] = domainInterface.decodeFunctionResult(
    "computeTokenHashForTree",
    raw,
  );
  if (BigInt(domain) !== note.tokenHash) {
    fail(
      "deposit merge receipt token domain does not match the replacement note",
    );
  }
  const after = await provider.getBlock(receipt.blockNumber);
  if (
    after?.number !== receipt.blockNumber ||
    after.hash?.toLowerCase() !== receipt.blockHash.toLowerCase()
  ) {
    fail("deposit merge receipt changed during verification");
  }
  return event;
}
